<template>
  <div class="w-full flex flex-col justify-center items-center gap-10">
    <div class="flex justify-between gap-4 w-full">
      <div
        v-for="(item, i) in stepList"
        :key="i"
        class="rounded-4xl flex flex-col items-center py-5 gap-4 flex-1"
        :class="[item.ready ? 'bg-key ring-2 ring-brand-blue1' : 'bg-neutral-light-default']"
      >
        <div class="flex flex-col items-center text-black-white">
          <div class="text-s-medium">{{ item.title }}</div>
          <div class="text-m-medium text-center">{{ item.content }}</div>
        </div>
        <img :src="item.img" class="w-full object-contain object-center" />
      </div>
    </div>
    <div class="flex flex-col justify-center items-center gap-2">
      <ConnectDeviceButton>
        {{ t('setting.pairing.addDeviceBtn') }}
      </ConnectDeviceButton>

      <div v-show="nimietyTips" class="text-center text-state-failure text-s-medium">
        {{ t('setting.pairing.multipleReceiverTip') }}
      </div>
    </div>

    <PairingDialog
      ref="pairingRef"
      :status="pairingStatus"
      :device-type="deviceType"
      :hide-instruction="pairingConfig.hideInstruction"
      @restart="startPairing"
      @reset="resetPairing"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, onUnmounted, ref, watch } from 'vue'

  import PairingDialog from './pairingDialog.vue'
  import step1 from '@/assets/images/header/settings/pairing/step1.png'
  import step2Mouse from '@/assets/images/header/settings/pairing/step2-mouse.png'
  import step2Keyboard from '@/assets/images/header/settings/pairing/step2-keyboard.png'

  import { useDeviceStore, useLocaleStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import { BaseDevice } from '@rapoo/device-sdk'
  import ConnectDeviceButton from './ConnectDeviceButton.vue'

  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  onUnmounted(() => {
    deviceStore.setPairingContext(null)
  })
  const { originDeviceList, deviceList } = storeToRefs(deviceStore)

  const props = defineProps<{
    device: any
  }>()

  const deviceType = computed(() => props.device.manifest.type || 'mouse')
  const pairingConfig = computed(() => {
    const capability = props.device?.manifest.capabilities.find(
      (item: { type: string }) => item.type === 'pairing',
    )
    const config = capability?.config as
      | { hideStep2?: boolean; hideInstruction?: boolean }
      | undefined
    return {
      hideStep2: Boolean(config?.hideStep2),
      hideInstruction: Boolean(config?.hideInstruction),
    }
  })

  const emit = defineEmits(['reset'])

  const stepList = computed(() => {
    const list = [
      {
        title: t('setting.pairing.step1.title'),
        content: t('setting.pairing.step1.content'),
        img: step1,
        ready: acceptors.value.length === 1,
      },
    ]

    if (!pairingConfig.value.hideStep2) {
      list.push({
        title: t('setting.pairing.step2.title'),
        content: t('setting.pairing.step2.content'),
        img: deviceType.value === 'mouse' ? step2Mouse : step2Keyboard,
        ready: !isOnine.value,
      })
    }

    return list
  })

  // 接收器列表
  const acceptors = computed(() => {
    if (!props.device) {
      return [] as BaseDevice[]
    }

    // 进行过滤
    function findPairingTargets(selected: BaseDevice, devices: BaseDevice[]): BaseDevice[] {
      const { currentPid, vendorId, pairingProductIds = [] } = selected.manifest.hardware
      const targets = new Map<string, BaseDevice>()

      for (const device of devices) {
        const target = device
        const hardware = target.manifest.hardware
        if (
          hardware.currentPid === currentPid ||
          hardware.vendorId !== vendorId ||
          !target.features.pairing
        ) {
          continue
        }

        const productIds = Array.isArray(hardware.productId)
          ? hardware.productId
          : [hardware.productId]
        const extraTarget = pairingProductIds.includes(hardware.currentPid)
        const receiverTarget = target.manifest.type !== 'base' && productIds.includes(currentPid)
        if (extraTarget || receiverTarget) targets.set(target.id, target)
      }

      return [...targets.values()]
    }

    return findPairingTargets(props.device, originDeviceList.value)
  })

  // 数量过多提示
  const nimietyTips = computed(() => {
    return acceptors.value.length > 1
  })

  // 当前设备是否在线
  const isOnine = computed(() => {
    const selectedDevice = props.device as BaseDevice
    const pid = selectedDevice.manifest.hardware.currentPid
    const vid = selectedDevice.manifest.hardware.vendorId
    const device = deviceList.value.find((device: BaseDevice) => {
      const dpid = device.manifest.hardware.currentPid
      const dvid = device.manifest.hardware.vendorId
      return dpid === pid && dvid === vid
    })

    return !!device
  })

  const pairingStatus = ref(0) // 0 可配对 1 配对中 2 配对成功 3 配对失败
  const updatePairingContext = () => {
    const target = acceptors.value.length === 1 ? acceptors.value[0] : undefined
    const source = props.device as BaseDevice | null
    deviceStore.setPairingContext(
      source && target
        ? {
            kind: target.manifest.type === 'base' ? 'base' : 'receiver',
            status: 'pairing',
            source: {
              vendorId: source.manifest.hardware.vendorId,
              productId: source.manifest.hardware.currentPid,
            },
            target: {
              vendorId: target.manifest.hardware.vendorId,
              productId: target.manifest.hardware.currentPid,
            },
          }
        : null,
    )
  }
  // 等待拔线期间跟随唯一目标；开始配对后保留 context，覆盖底座重枚举窗口。
  watch(
    [() => props.device, acceptors],
    () => {
      if (pairingStatus.value === 0) updatePairingContext()
    },
    { immediate: true, flush: 'sync' },
  )
  // 自动开始配对
  watch(
    () => stepList.value,
    async (_val) => {
      if (pairingStatus.value !== 0) {
        console.log('暂不可配对')
        return
      }
      startPairing()
    },
  )

  const pairingRef = ref<InstanceType<typeof PairingDialog>>()
  const startPairing = async () => {
    if (pairingStatus.value === 1) return
    const allReady = stepList.value.every((item) => item.ready)
    if (allReady) {
      const acceptor = acceptors.value[0]
      const pairing = acceptor.features.pairing
      if (pairing) {
        updatePairingContext()
        pairingRef.value?.openDialog()
        pairingStatus.value = 1
        try {
          const res = await pairing.start()
          if (res) deviceStore.setPairingContextStatus('completed')
          pairingStatus.value = res ? 2 : 3
        } catch (_error) {
          pairingStatus.value = 3
        }
      }
    }
  }

  const resetPairing = () => {
    deviceStore.setPairingContext(null)
    emit('reset')
    pairingStatus.value = 0
  }
</script>
